import { GridItemTrafficLight, IRequestStatus, MosaicTrafficLightsValue } from "aic";
import { CommentFilterOptionValue } from "components/common/comments/interface";
import { TocItemUpdateMessage } from "contexts/signalr/interface";
import { TlfUpdateMessage } from "contexts/tlf/interface";
import { eShowTiFo, eTlfItemType } from "functions/api/enums";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { ITocSection } from "functions/api/hierarchy/ITocSection";
import { TitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import type { TocItemsUpdateProps } from "functions/api/toc/updateTocItems";
import { Dispatch } from "react";
import { MosaicTocSectionClass } from "./MosaicTocSectionClass";

export interface IMosaicToc {
  hierarchyId: string;
  tocItems: (MosaicTocSectionClass | MosaicTocEntryDto)[];
  removedItems: IRemovedItemProps[];
  saving: boolean;
  showTypes: eTlfItemType[];
  showTiFo: eShowTiFo;
  commentsFilter: CommentFilterOptionValue;
  trafficLightsFilter?: MosaicTrafficLightsValue;
}

export interface IMosaicTocExpanded extends IMosaicToc {
  blocked: "getNewVersion" | false;
  changed: boolean;
  readOnly: boolean;
  receivedMessages: TocUpdateMessage[];
  saveError: boolean;
  sendUpdate: boolean;
  searchText: string;
  lastMinorVersionSavedAt: number | null;
  lastMajorVersionSavedAt: number;
  /** Request status for the initial ToC load, driven by {@link useHierarchyEffects} */
  tocRequestStatus: IRequestStatus<HierarchyQueryParams>;
  /** Whether the "New Major Version Available" popup should be shown */
  showSaveAlert: boolean;
  /** Hierarchy id received via a `tocSave` SignalR message, used to navigate on popup close */
  tocIdFromMessage?: string;
  /** Whether the "Large change made" (tooBig) popup should be shown */
  showNeedRefresh: boolean;
}

export interface IRemovedItemProps {
  id: string;
  changed: boolean;
  azsolId?: string;
  version?: number;
}

export interface TocUpdateMessage {
  messageId: string;
  updatedIds: string[];
  updateTS: Date;
}

export interface TocContextProps {
  state: IMosaicTocExpanded;
  dispatch: Dispatch<ITocAction>;
  tocRequest: IRequestStatus<HierarchyQueryParams>;
}

export interface MosaicTocBaseDto {
  id: string;
  primaryHierarchyId: string;
  tocNumber?: string;
  tocLabel?: string;
  tocOrder?: number;
  changed: boolean;
  inError: boolean;
}

export interface ITocSectionExpanded extends ITocSection {
  primaryHierarchyId: string;
}

export interface MosaicTocSectionDto extends MosaicTocBaseDto {
  type: eTlfItemType.section;
}

export interface IMosaicTocItemUpdate {
  id: string;
  saved: boolean;
  primaryHierarchyId?: string;
  tocNumber?: string;
  tocLabel?: string;
  tocOrder?: number;
  version?: number;
}

export interface MosaicTocEntryDto extends TitlesFootnotesReadDto, MosaicTocBaseDto {
  changed: boolean;
  inError: boolean;
  changedTitles: GridItemTrafficLight[];
}

export type MosaicTocItemDto = MosaicTocSectionDto | TitlesFootnotesReadDto;

export const ADD_NEW_TOC_ENTRIES = "ADD_NEW_TOC_ENTRIES";
export const ADD_SECTION = "ADD_SECTION";
export const CLEAR_REMOVED = "CLEAR_REMOVED";
export const CLOSE_TOC_NEED_REFRESH = "CLOSE_TOC_NEED_REFRESH";
export const CLOSE_TOC_SAVE_ALERT = "CLOSE_TOC_SAVE_ALERT";
export const DELETE = "DELETE";
export const FROM_SIGNALR = "FROM_SIGNALR";
export const MOVE_ITEM = "MOVE_ITEM";
export const REASSIGN_TOC_NUMBERS = "REASSIGN_TOC_NUMBERS";
export const REFRESH = "REFRESH";
export const SAVE_FAIL = "SAVE_FAIL";
export const SAVE_SUCCESS = "SAVE_SUCCESS";
export const SET_TOC_REQUEST_STATUS = "SET_TOC_REQUEST_STATUS";
export const SET_TYPE_FILTER = "SET_TYPE_FILTER";
export const SHOW_TOC_NEED_REFRESH = "SHOW_TOC_NEED_REFRESH";
export const SHOW_TOC_SAVE_ALERT = "SHOW_TOC_SAVE_ALERT";
export const SORT_TOC = "SORT_TOC";
export const UPDATE_AFTER_PATCH = "UPDATE_AFTER_PATCH";
export const UPDATE_COMMENTS_FILTER = "UPDATE_COMMENTS_FILTER";
export const UPDATE_ENTRY = "UPDATE_ENTRY";
export const UPDATE_LAST_MAJOR_VERSION_SAVED_AT = "UPDATE_LAST_MAJOR_VERSION_SAVED_AT";
export const UPDATE_SEARCH_TEXT = "UPDATE_SEARCH_TEXT";
export const UPDATE_SECTION = "UPDATE_SECTION";
export const UPDATE_TRAFFIC_LIGHTS_FILTER = "UPDATE_TRAFFIC_LIGHTS_FILTER";
export const UPDATE_VIEW_TIFO = "UPDATE_VIEW_TIFO";

export type TocOperation =
  | typeof ADD_SECTION
  | typeof CLEAR_REMOVED
  | typeof DELETE
  | typeof FROM_SIGNALR
  | typeof ADD_NEW_TOC_ENTRIES
  | typeof MOVE_ITEM
  | typeof REASSIGN_TOC_NUMBERS
  | typeof REFRESH
  | typeof SAVE_FAIL
  | typeof SAVE_SUCCESS
  | typeof SET_TYPE_FILTER
  | typeof SET_TOC_REQUEST_STATUS
  | typeof SHOW_TOC_SAVE_ALERT
  | typeof CLOSE_TOC_SAVE_ALERT
  | typeof SHOW_TOC_NEED_REFRESH
  | typeof CLOSE_TOC_NEED_REFRESH
  | typeof SORT_TOC
  | typeof UPDATE_AFTER_PATCH
  | typeof UPDATE_COMMENTS_FILTER
  | typeof UPDATE_ENTRY
  | typeof UPDATE_LAST_MAJOR_VERSION_SAVED_AT
  | typeof UPDATE_TRAFFIC_LIGHTS_FILTER
  | typeof UPDATE_SEARCH_TEXT
  | typeof UPDATE_SECTION
  | typeof UPDATE_VIEW_TIFO;

export interface ITocActionBase {
  operation: TocOperation;
}

export interface ITocActionNoParams extends ITocActionBase {
  operation:
    | typeof CLEAR_REMOVED
    | typeof REASSIGN_TOC_NUMBERS
    | typeof SAVE_FAIL
    | typeof SORT_TOC
    | typeof UPDATE_AFTER_PATCH
    | typeof CLOSE_TOC_SAVE_ALERT
    | typeof SHOW_TOC_NEED_REFRESH
    | typeof CLOSE_TOC_NEED_REFRESH;
}

export interface ITocActionAddSection extends ITocActionBase {
  operation: typeof ADD_SECTION;
  newId: string;
}

export interface ITocActionDelete extends ITocActionBase {
  operation: typeof DELETE;
  itemId: string;
}

export interface ITocActionFromSignalR extends ITocActionBase {
  operation: typeof FROM_SIGNALR;
  items: TocItemUpdateMessage[];
  deletedItemId?: string;
  receivedMessage: TocUpdateMessage;
}

export interface ITlfActionFromSignalR extends ITocActionBase {
  operation: typeof FROM_SIGNALR;
  item: string;
  updatedItemId?: string;
  receivedMessage: TlfUpdateMessage;
}

export interface ITocActionLoadEntries extends ITocActionBase {
  operation: typeof ADD_NEW_TOC_ENTRIES;
  entries: MosaicTocEntryDto[];
  loadTimeStamp: number;
}

export interface ITocActionMoveItem extends ITocActionBase {
  operation: typeof MOVE_ITEM;
  itemId: string;
  newIndex: number;
}

export interface ITocActionRefresh extends ITocActionBase {
  operation: typeof REFRESH;
  hierarchyId: string;
  items: (MosaicTocEntryDto | MosaicTocSectionClass)[];
  removedItems?: IRemovedItemProps[];
}

export interface ITocActionSetTypeFilter extends ITocActionBase {
  operation: typeof SET_TYPE_FILTER;
  showTypes: eTlfItemType[];
}

export interface ITocActionSetRequestStatus extends ITocActionBase {
  operation: typeof SET_TOC_REQUEST_STATUS;
  tocRequestStatus: IRequestStatus<HierarchyQueryParams>;
}

export interface ITocActionShowSaveAlert extends ITocActionBase {
  operation: typeof SHOW_TOC_SAVE_ALERT;
  tocIdFromMessage: string;
}

export interface ITocActionUpdateCommentsFilter extends ITocActionBase {
  operation: typeof UPDATE_COMMENTS_FILTER;
  commentsFilter: CommentFilterOptionValue;
}

export interface ITocActionUpdateLastMajorVersionSavedAt extends ITocActionBase {
  operation: typeof UPDATE_LAST_MAJOR_VERSION_SAVED_AT;
  savedTimeStamp: Date;
}

export interface ITocActionUpdateTrafficLightsFilter extends ITocActionBase {
  operation: typeof UPDATE_TRAFFIC_LIGHTS_FILTER;
  trafficLightFilter?: MosaicTrafficLightsValue;
}

export interface ITocActionUpdateEntry extends ITocActionBase {
  operation: typeof UPDATE_ENTRY;
  update: TitlesFootnotesReadDto;
}

export interface ITocActionUpdateSection extends ITocActionBase {
  operation: typeof UPDATE_SECTION;
  update: IMosaicTocItemUpdate;
}

export interface ITocActionSaveSuccess extends ITocActionBase {
  operation: typeof SAVE_SUCCESS;
  itemsUpdate?: TocItemsUpdateProps;
  saveTimeStamp: number;
}

export interface ITocActionUpdateSearchText extends ITocActionBase {
  operation: typeof UPDATE_SEARCH_TEXT;
  searchText: string;
}

export interface ITocActionUpdateViewTiFo extends ITocActionBase {
  operation: typeof UPDATE_VIEW_TIFO;
  showTiFo: eShowTiFo;
}

export type ITocAction =
  | ITocActionNoParams
  | ITocActionAddSection
  | ITocActionDelete
  | ITocActionFromSignalR
  | ITocActionLoadEntries
  | ITocActionMoveItem
  | ITocActionRefresh
  | ITocActionSaveSuccess
  | ITocActionSetTypeFilter
  | ITocActionSetRequestStatus
  | ITocActionShowSaveAlert
  | ITocActionUpdateEntry
  | ITocActionUpdateCommentsFilter
  | ITocActionUpdateTrafficLightsFilter
  | ITocActionUpdateLastMajorVersionSavedAt
  | ITocActionUpdateSearchText
  | ITocActionUpdateSection
  | ITocActionUpdateViewTiFo;
